import { useRef, useState } from "react";
import { useAddEvidence, useChecks, useSaveChecks, type CheckItem, type ChecksView } from "../../api/inspections";
import EvidencePanel from "./EvidencePanel";

type Answer = { result: string | null; notes: string | null };
type Answers = Record<string, Answer>;

const OPTIONS: Record<CheckItem["kind"], readonly { value: string; label: string }[]> = {
  tick: [
    { value: "done", label: "Done" },
    { value: "n/a", label: "N/A" },
  ],
  yes_no: [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
    { value: "n/a", label: "N/A" },
  ],
  // The PM42 procedure's P / F column.
  pass_fail: [
    { value: "pass", label: "Pass" },
    { value: "fail", label: "Fail" },
    { value: "n/a", label: "N/A" },
  ],
};

/** The PM41 checklist's section 5 is the photographic record: every item is a
 * photograph to take, so each has a camera. The server says which sections are
 * photographic (`section.photographic`); this is the fallback for one that does
 * not. */
export const PHOTO_SECTION = "5";

/** The visit's checks for one valve, and the evidence attached to the visit.
 * A tick item is done or n/a; a yes/no item takes yes, no or n/a; a PM42 step
 * takes pass, fail or n/a. */
export default function ChecksTab({
  inspectionId,
  valveId,
  valveName,
  canEdit,
  onlyOpen,
  onOnlyOpen,
}: {
  inspectionId: number;
  valveId: number | null;
  valveName: string | null;
  canEdit: boolean;
  onlyOpen: boolean;
  onOnlyOpen: (on: boolean) => void;
}) {
  const view = useChecks(inspectionId, valveId);
  if (view.isPending) {
    return <p className="app-loading">Loading checks…</p>;
  }
  if (view.isError) {
    return <p className="notice bad">The checks could not be loaded: {view.error.message}</p>;
  }
  return <ChecksPanel key={view.data.active_valve_id ?? 0} view={view.data} valveName={valveName} canEdit={canEdit} onlyOpen={onlyOpen} onOnlyOpen={onOnlyOpen} />;
}

export function answersFrom(view: ChecksView): Answers {
  const answers: Answers = {};
  for (const section of view.sections) {
    for (const item of section.items) {
      answers[item.code] = { result: item.result, notes: item.notes };
    }
  }
  return answers;
}

/** The questions, answered with one tap each, saved as they are answered:
 * the tap is the save, and a note saves when the box is left. Only the item
 * that changed is sent, so two people on one valve cannot wipe each other's
 * answers. Tapping the chosen answer again clears it. A note box is opened
 * on request, so 33 items read as 33 lines rather than 66; a photograph item
 * has a camera, and the photo it takes ticks the item. */
export function ChecksPanel({
  view,
  valveName,
  canEdit,
  onlyOpen,
  onOnlyOpen,
}: {
  view: ChecksView;
  valveName: string | null;
  canEdit: boolean;
  onlyOpen: boolean;
  onOnlyOpen: (on: boolean) => void;
}) {
  const save = useSaveChecks(view.inspection.id);
  const attach = useAddEvidence(view.inspection.id);
  const [answers, setAnswers] = useState<Answers>(() => answersFrom(view));
  const [failed, setFailed] = useState<{ code: string; message: string } | null>(null);
  const [noting, setNoting] = useState<ReadonlySet<string>>(() => new Set());
  const [shooting, setShooting] = useState<string | null>(null);
  const camera = useRef<HTMLInputElement>(null);
  const edit = canEdit && view.is_draft;
  // A step the form's own rules take off this valve (PM42 C-26) is not asked.
  const sections = view.sections.map((s) => ({ ...s, items: s.items.filter((i) => i.applicable !== false) }));
  const items = sections.flatMap((s) => s.items);
  const answered = items.filter((i) => answers[i.code]?.result).length;
  const photosFor = (code: string) => view.evidence.filter((e) => e.check_code === code).length;

  function send(code: string, next: Answer) {
    const before = answers[code] ?? { result: null, notes: null };
    setAnswers((a) => ({ ...a, [code]: next }));
    setFailed(null);
    save.mutate(
      { valve_id: view.active_valve_id, answers: { [code]: next } },
      {
        onError: (error) => {
          setAnswers((a) => ({ ...a, [code]: before }));
          setFailed({ code, message: error.message });
        },
      },
    );
  }
  function choose(code: string, value: string) {
    const current = answers[code] ?? { result: null, notes: null };
    send(code, { result: current.result === value ? null : value, notes: current.notes });
  }
  function note(code: string, text: string) {
    setAnswers((a) => ({ ...a, [code]: { result: a[code]?.result ?? null, notes: text } }));
  }
  /** A note is stored with its answer: the server keeps no row for an
   * unanswered check, so a note typed first goes up with the answer. */
  function saveNote(item: CheckItem) {
    const current = answers[item.code] ?? { result: null, notes: null };
    const text = current.notes?.trim() || null;
    if (current.result !== null && text !== (item.notes ?? null)) {
      send(item.code, { result: current.result, notes: text });
    }
  }
  function openNote(code: string) {
    setNoting((set) => new Set([...set, code]));
  }
  /** The photo is the item: once it is attached the item is done. */
  function shoot(file: File) {
    const code = shooting;
    setShooting(null);
    if (code === null) {
      return;
    }
    attach.mutate(
      { valveId: view.active_valve_id, file, caption: "", checkCode: code },
      {
        onSuccess: () => {
          if (!answers[code]?.result) {
            send(code, { result: "done", notes: answers[code]?.notes ?? null });
          }
        },
        onError: (error) => setFailed({ code, message: error.message }),
      },
    );
  }

  return (
    <div className="insp-section">
      <div className="insp-section-head">
        <h3>Checks{valveName ? ` · ${valveName}` : ""}</h3>
        <span className="insp-section-sub">
          {answered} of {items.length} answered{edit ? " · saves as you go" : ""}
        </span>
        <label className="check insp-only-open">
          <input type="checkbox" checked={onlyOpen} onChange={(e) => onOnlyOpen(e.target.checked)} /> Only unanswered
        </label>
      </div>
      {failed ? (
        <p className="notice bad">
          {failed.code} was not saved: {failed.message}
        </p>
      ) : null}
      {onlyOpen && answered === items.length ? <p className="notice ok">Every check on this valve is answered.</p> : null}
      <input
        ref={camera}
        type="file"
        hidden
        accept="image/*"
        capture="environment"
        aria-label="Photograph for the check"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            shoot(file);
          }
          e.target.value = "";
        }}
      />
      {sections.map((section) => {
        const shown = section.items.filter((item) => !onlyOpen || !answers[item.code]?.result);
        if (shown.length === 0) {
          return null;
        }
        const photographic = section.photographic ?? section.section === PHOTO_SECTION;
        return (
          <section key={section.section} className="check-group" aria-label={`Section ${section.section}`}>
            <h4 className="check-group-title">{section.title}</h4>
            {photographic && edit ? <p className="insp-hint">Take each photograph with its camera; the item is ticked when the photo is in.</p> : null}
            {shown.map((item) => {
              const answer = answers[item.code];
              const photos = photosFor(item.code);
              const showNote = noting.has(item.code) || !!answer?.notes;
              return (
                <div key={item.code} className={answer?.result ? "check-item" : "check-item open"}>
                  <span className="check-code mono">{item.display_code ?? item.code}</span>
                  <div className="check-text">
                    <span className="check-label" id={`check-${item.code}`}>
                      {item.label}
                      {photos > 0 ? <span className="check-photos">{photos === 1 ? "1 photo" : `${photos} photos`}</span> : null}
                    </span>
                    {showNote ? (
                      <input
                        className="check-note"
                        value={answer?.notes ?? ""}
                        onChange={(e) => note(item.code, e.target.value)}
                        onBlur={() => saveNote(item)}
                        placeholder={edit ? (answer?.result ? "Note" : "Note (saved with the answer)") : ""}
                        readOnly={!edit}
                        autoFocus={noting.has(item.code) && !answer?.notes}
                        aria-label={`Note for ${item.code}`}
                      />
                    ) : null}
                  </div>
                  <div className="check-answers" role="group" aria-labelledby={`check-${item.code}`}>
                    {photographic && edit ? (
                      <button
                        type="button"
                        className="answer-photo"
                        aria-label={`Photograph: ${item.label}`}
                        disabled={attach.isPending}
                        onClick={() => {
                          setShooting(item.code);
                          camera.current?.click();
                        }}
                      >
                        {attach.isPending && shooting === item.code ? "…" : "📷"}
                      </button>
                    ) : null}
                    {OPTIONS[item.kind].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`answer-${option.value === "n/a" ? "na" : option.value}`}
                        aria-pressed={answer?.result === option.value}
                        disabled={!edit}
                        onClick={() => choose(item.code, option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                    {edit && !showNote ? (
                      <button type="button" className="answer-note" aria-label={`Add a note to ${item.code}`} onClick={() => openNote(item.code)}>
                        Note
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </section>
        );
      })}
      <EvidencePanel view={view} edit={edit} />
    </div>
  );
}
